import { m } from '@pricedrop/i18n';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';
import XCircle from 'lucide-react/dist/esm/icons/x-circle';
import * as React from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { authClient } from '@/lib/auth-client';
import { buildMeta } from '@/lib/seo';

const verifySearchSchema = z.object({
  token: z.string(),
  callbackURL: z.string().optional(),
});

export const Route = createFileRoute('/_public/auth/verify')({
  head: () => ({
    meta: buildMeta({
      title: 'אימות חשבון - Pricedrop',
      description: '',
      canonicalUrl: '/auth/verify',
      noindex: true,
    }),
  }),
  validateSearch: verifySearchSchema,
  component: VerifyPage,
});

type VerifyState = 'verifying' | 'success' | 'error';

const VERIFIED_TOKENS_KEY = 'app:verified_tokens';
const PENDING_TOKENS_KEY = 'app:pending_tokens';

function getStoredSet(key: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function addToStoredSet(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  const set = getStoredSet(key);
  set.add(value);
  sessionStorage.setItem(key, JSON.stringify([...set]));
}

function removeFromStoredSet(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  const set = getStoredSet(key);
  set.delete(value);
  sessionStorage.setItem(key, JSON.stringify([...set]));
}

function isInStoredSet(key: string, value: string): boolean {
  return getStoredSet(key).has(value);
}

function VerifyPage() {
  const { token, callbackURL } = Route.useSearch();
  const navigate = useNavigate();
  const [state, setState] = React.useState<VerifyState>(() => {
    return isInStoredSet(VERIFIED_TOKENS_KEY, token) ? 'success' : 'verifying';
  });
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  React.useEffect(() => {
    if (state === 'success' || isInStoredSet(VERIFIED_TOKENS_KEY, token)) {
      navigate({ to: callbackURL || '/', replace: true });
      return;
    }

    if (isInStoredSet(PENDING_TOKENS_KEY, token)) {
      return;
    }

    addToStoredSet(PENDING_TOKENS_KEY, token);

    async function verify() {
      const { data, error } = await authClient.verifyEmail({
        query: { token },
      });

      removeFromStoredSet(PENDING_TOKENS_KEY, token);

      if (isInStoredSet(VERIFIED_TOKENS_KEY, token)) {
        return;
      }

      if (error) {
        setState('error');
        setErrorMessage(error.message || m.verify_failed());
        return;
      }

      if (!data?.status) {
        setState('error');
        setErrorMessage(m.verify_failed());
        return;
      }

      addToStoredSet(VERIFIED_TOKENS_KEY, token);
      setState('success');
      navigate({ to: callbackURL || '/', replace: true });
    }

    verify();
  }, [token, callbackURL, navigate, state]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">
            {state === 'verifying' && m.verify_verifying()}
            {state === 'success' && m.verify_success()}
            {state === 'error' && m.verify_failed()}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          {state === 'verifying' && (
            <>
              <Loader2 className="size-12 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">{m.verify_pleaseWait()}</p>
            </>
          )}

          {state === 'success' && (
            <>
              <CheckCircle className="size-12 text-green-500" />
              <p className="text-sm text-muted-foreground">{m.verify_signedIn()}</p>
            </>
          )}

          {state === 'error' && (
            <>
              <XCircle className="size-12 text-destructive" />
              <p className="text-sm text-muted-foreground text-center">{errorMessage}</p>
              <Button variant="outline" onClick={() => navigate({ to: '/login' })} className="mt-2">
                {m.verify_backToLogin()}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
